import React from 'react';
import styled, { css } from 'styled-components';

type BaseCardProps = {
  $interactive?: boolean;
  $padding?: string;
};

const CardBase = styled.div<BaseCardProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  padding: ${({ $padding }) => $padding ?? 'var(--spacing-6)'};
  background: linear-gradient(155deg, rgba(40, 40, 40, 0.88) 0%, rgba(18, 18, 18, 0.94) 100%);
  border-radius: var(--radius-xl);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 var(--spacing-6) var(--spacing-10) rgba(0, 0, 0, 0.55);
  color: var(--color-text-primary);
  transition: transform 0.35s var(--transition-smooth), box-shadow 0.35s var(--transition-smooth), border-color 0.35s var(--transition-smooth), background 0.35s var(--transition-smooth), filter 0.35s var(--transition-smooth);
  overflow: hidden;
  backdrop-filter: blur(0.75rem);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 0.0625rem; /* 1px - fine border */
    background: linear-gradient(140deg, rgba(255, 255, 255, 0.14), rgba(255, 43, 43, 0.22), rgba(255, 255, 255, 0.08));
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    pointer-events: none;
    opacity: 0.55;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 18% 20%, rgba(255, 77, 77, 0.27), transparent 60%);
    opacity: 0.45;
    pointer-events: none;
  }

  ${({ $interactive }) =>
    $interactive &&
    css`
      cursor: pointer;
      border-color: rgba(255, 255, 255, 0.16);

      &:hover {
        transform: translateY(-0.625rem);
        border-color: rgba(255, 77, 77, 0.45);
        box-shadow: 0 var(--spacing-6) var(--spacing-12) rgba(255, 43, 43, 0.25);
        filter: brightness(1.04);

        &::after {
          opacity: 0.85;
        }
      }

      &:active {
        transform: translateY(-0.25rem);
        filter: brightness(0.98);
      }
    `}
`;

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  interactive?: boolean;
  padding?: string;
};

export const Card = React.memo(React.forwardRef<HTMLDivElement, CardProps>(
  ({ interactive, padding, children, ...rest }, ref) => (
    <CardBase 
      ref={ref} 
      $interactive={interactive} 
      $padding={padding} 
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      {...rest}
    >
      {children}
    </CardBase>
  )
));

Card.displayName = 'Card';

